/*
  assets/mad-quote-sheet-core.js (Worker S)
  Data and cart layer for the collection "Price this door" sheet plus the sticky
  "Your estimate" bar. No dialog DOM in here (that is mad-quote-sheet.js).
  Depends on window.MADEstimate and window.MAD_PRICE_BOOK (loaded by the snippet).
  Follows docs/wizard-contract.md sections 3, 5, 7.6 and 8.
*/
(function () {
  'use strict';
  if (window.MadSheetCore) return;

  var M = function () { return window.MADEstimate; };
  var BOOK = function () { return window.MAD_PRICE_BOOK || {}; };
  var CFG = function () { return window.MadQuoteConfig || {}; };

  function ready() {
    return new Promise(function (resolve, reject) {
      var t0 = Date.now();
      (function poll() {
        if (window.MADEstimate && window.MAD_PRICE_BOOK) return resolve();
        if (Date.now() - t0 > 6000) return reject(new Error('MADEstimate or MAD_PRICE_BOOK did not load'));
        setTimeout(poll, 40);
      })();
    });
  }

  /* ── product object (contract section 3) ─────────────────────── */
  function productFromElement(el) {
    var host = el && el.closest ? el.closest('[data-mad-product]') : null;
    if (!host) return null;
    try { return JSON.parse(host.getAttribute('data-mad-product')); } catch (e) { return null; }
  }

  function ctxFor(product) {
    var E = M();
    var config = E.doorConfig(product.tags) || 'single';
    var flags = E.doorFlags(product.tags);
    return {
      config: config,
      flags: flags,
      kind: E.detectDoorKind(product),
      slab: E.trustedSlab(product), /* R04: the size the card and the product page show, never a fresh parse of the title */
      doorPrice: (Number(product.price) || 0) / 100,
    };
  }

  function installReadyPrice(product, intExt) {
    var E = M(), c = ctxFor(product);
    var raw = E.defaultInstallRawFor(c.config, c.flags, intExt || 'interior', 'standard_hinged', BOOK());
    return E.prehangPrice(raw, BOOK());
  }

  function resizePrice() { return Number((BOOK().prices || {}).resize_yes) || 0; }

  /* ── answers -> fit readout and _raw (contract 7.6) ─────────── */
  // answers: { intExt, openingWidth, openingHeight, notMeasured, jambNeeded, resize }
  function openingFrom(answers) {
    var E = M();
    if (answers.notMeasured) return null;
    var w = E.parseInches(answers.openingWidth), h = E.parseInches(answers.openingHeight);
    return w && h ? { width: w, height: h } : null;
  }

  function fitFor(product, answers) {
    var c = ctxFor(product);
    return M().fitReadout({ slab: c.slab, opening: openingFrom(answers), openingType: 'rough', flags: c.flags, book: BOOK() });
  }

  function quickRaw(product, answers) {
    var E = M(), c = ctxFor(product), a = answers || {};
    var raw = { door_kind: c.kind, door_config: c.config };
    if (c.slab) raw.slab_size = E.formatInches(c.slab.width) + 'x' + E.formatInches(c.slab.height); // same form as the wizard, so an Edit round trip keeps it
    raw.int_ext = a.intExt === 'exterior' ? 'exterior' : 'interior';
    raw.application = 'standard_hinged';
    var opening = openingFrom(a);
    if (opening) {
      raw.opening_type = 'rough';
      raw.opening_width = String(a.openingWidth).trim();
      raw.opening_height = String(a.openingHeight).trim();
      raw.rough_opening = E.formatInches(opening.width) + ' x ' + E.formatInches(opening.height);
    } else {
      raw.opening_type = 'none';
      raw.not_measured = '1';
    }
    var fit = fitFor(product, a);
    raw.fit_readout = fit.state;
    var resizeYes = fit.state === 'resize' && a.resize !== 'no';
    raw.resize = resizeYes ? 'yes' : 'no';
    if (resizeYes && fit.suggestedTarget) {
      raw.target_width = E.formatInches(fit.suggestedTarget.width);
      raw.target_height = E.formatInches(fit.suggestedTarget.height);
    }
    if (a.jambNeeded === 'yes') {
      // 7.3 order: jamb_needed, details_deferred, jamb_type, wall_depth, swing, hinge_color, latch_type, jamb_grade
      var d = E.defaultInstallRawFor(c.config, c.flags, raw.int_ext, 'standard_hinged', BOOK());
      raw.jamb_needed = 'yes';
      raw.details_deferred = 'yes';
      ['jamb_type', 'wall_depth', 'swing', 'hinge_color', 'latch_type', 'jamb_grade'].forEach(function (k) {
        if (d[k] !== '' && d[k] != null || k === 'hinge_color') raw[k] = d[k] == null ? '' : d[k];
      });
    } else {
      raw.jamb_needed = a.jambNeeded === 'not_sure' ? 'not_sure' : 'no';
    }
    // Sheet keeps original glass: antique + Glass tag needs the non-tempered ack when collected.
    if (c.kind === 'antique' && c.flags.glass && a.glassCompliance) {
      raw.glass_work = 'none';
      raw.glass_compliance = '1';
    }
    var est = estimateFor(raw, product);
    raw.estimate_total = est.total.toFixed(2);
    raw.price_book_version = String(BOOK().version || '');
    return raw;
  }

  function estimateFor(raw, product) {
    var c = ctxFor(product);
    return M().estimate(raw, { doorPrice: c.doorPrice, config: c.config, flags: c.flags, kind: c.kind }, BOOK());
  }

  /* ── line-item properties (contract section 8) ──────────────── */
  // MadWizard.buildProperties is the owner (W). This fallback mirrors W's labels one for one
  // (mad-wizard-cart.js) for the keys a quick-questions raw can carry, for pages where W's script is absent.
  var JAMB = { yes: 'Yes', no: 'No, slab only', not_sure: 'Not sure' };
  var DEPTH = { '4_9_16': '4 9/16 in', '6_9_16': '6 9/16 in', other: 'Other', not_sure: 'Not sure' };
  var SWING = { not_sure: 'Not sure', lhis: 'Left hand in-swing', lhos: 'Left hand out-swing', rhis: 'Right hand in-swing', rhos: 'Right hand out-swing', lhais: 'Left hand active in-swing', lhaos: 'Left hand active out-swing', rhais: 'Right hand active in-swing', rhaos: 'Right hand active out-swing' };
  var HINGE = { '': 'Not sure', brushed_nickel: 'Brushed Nickel', gold: 'Brushed Gold', matte_black: 'Matte Black', antique_bronze: 'Antique Bronze', other: 'Other' };
  var LATCH = { magnetic: 'Magnetic', ball: 'Ball catch', no_latch: 'No latch' };
  var GRADE = { not_sure: 'Not sure', prime_pine: 'Primed pine', stain_cypress: 'Stain-grade cypress' };

  function buildProperties(raw, product, est) {
    if (window.MadWizard && typeof window.MadWizard.buildProperties === 'function') {
      return window.MadWizard.buildProperties(raw, product);
    }
    var E = M(), p = {};
    p['Location'] = raw.int_ext === 'exterior' ? 'Exterior' : 'Interior';
    p['Application'] = 'Standard hinged';
    if (raw.rough_opening) p['Rough Opening'] = raw.rough_opening;
    if (raw.not_measured === '1') p['Rough Opening'] = 'Not measured yet';
    if (raw.fit_readout && E.FIT_TEXT[raw.fit_readout]) p['Fit'] = E.FIT_TEXT[raw.fit_readout];
    p['Resize'] = raw.resize === 'yes' ? 'Yes' : 'None';
    if (raw.resize === 'yes' && raw.target_width) p['Target Width'] = raw.target_width + ' in';
    if (raw.resize === 'yes' && raw.target_height) p['Target Height'] = raw.target_height + ' in';
    if (raw.door_kind !== 'handcrafted') p['Restoration'] = 'None';
    p['Wax Finish'] = 'None';
    if (raw.glass_compliance === '1') p['Glass Safety Acknowledged'] = 'Yes';
    p['Pre-Hanging'] = JAMB[raw.jamb_needed] || JAMB.no;
    if (raw.jamb_needed === 'yes') {
      p['Jamb Type'] = String(raw.jamb_type || '').replace(/_/g, ' ');
      p['Jamb Depth'] = raw.wall_depth === 'other' && raw.wall_depth_custom ? raw.wall_depth_custom + ' in' : (DEPTH[raw.wall_depth] || 'Not sure');
      p['Swing'] = SWING[raw.swing] || 'Not sure';
      p['Hinge Color'] = HINGE[raw.hinge_color || ''] || raw.hinge_color;
      if (raw.int_ext !== 'exterior') p['Latch'] = LATCH[raw.latch_type] || 'Magnetic';
      p['Jamb Grade'] = GRADE[raw.jamb_grade] || 'Not sure';
      if (raw.details_deferred === 'yes') p['Install details'] = 'Ask me on the call';
    }
    if (raw.estimate_total) p['Estimate before freight'] = E.money(Number(raw.estimate_total));
    else p['Estimate before freight'] = E.money(est.total);
    p['_raw'] = JSON.stringify(raw);
    return p;
  }

  /* ── cart ───────────────────────────────────────────────────── */
  function fetchJson(url, opts) {
    return fetch(url, Object.assign({ credentials: 'same-origin' }, opts || {})).then(function (r) {
      return r.json().then(function (j) {
        if (!r.ok) { var e = new Error(j.description || j.message || ('Request failed (' + r.status + ')')); e.status = r.status; throw e; }
        return j;
      });
    });
  }

  function fetchCart() { return fetchJson('/cart.js', { headers: { Accept: 'application/json' } }); }

  // One request to /products/{handle}.js so a one-of-one that just sold is caught before /cart/add.js.
  function liveVariant(product) {
    if (!product.handle) return Promise.resolve(null);
    return fetchJson('/products/' + product.handle + '.js').then(function (pj) {
      var v = (pj.variants || []).filter(function (x) { return x.id === product.variantId; })[0] || (pj.variants || [])[0];
      if (!v) return null;
      return { variantId: v.id, available: !!v.available, price: v.price };
    }).catch(function () { return null; });
  }

  function updateBubble(sections) {
    var html = sections && sections['cart-icon-bubble'];
    var host = document.getElementById('cart-icon-bubble');
    if (!html || !host) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var inner = doc.querySelector('.shopify-section') || doc.body;
    host.innerHTML = inner.innerHTML;
  }

  function emit(name, detail) { document.dispatchEvent(new CustomEvent(name, { detail: detail || {} })); }

  function notifyCart(cart, extra) {
    emit('cart:refresh', { cart: cart });
    emit('cart:updated', { cart: cart });
    if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
      try { window.publish(window.PUB_SUB_EVENTS.cartUpdate, Object.assign({ source: 'mad-sheet', cartData: cart }, extra || {})); } catch (e) { /* theme listener error, not ours */ }
    }
    refreshBar(cart);
  }

  /* Plan 5.4: with the estimate list on (assets/mad-estimate-list.js) a door priced with services
     goes to the list, never into the payable cart. */
  function EL() { var L = window.MadEstimateList; return L && L.enabled ? L : null; }

  // Returns Promise<{ line, cart, estimate }>. Throws Error('SOLD_OUT') when the door is gone.
  function addToCart(product, raw) {
    return liveVariant(product).then(function (live) {
      if (live && live.available === false) { var so = new Error('SOLD_OUT'); so.code = 'SOLD_OUT'; throw so; }
      var variantId = (live && live.variantId) || product.variantId;
      var priced = live && typeof live.price === 'number' ? Object.assign({}, product, { price: live.price }) : product;
      var est = estimateFor(raw, priced);
      raw.estimate_total = est.total.toFixed(2);
      var props = buildProperties(raw, priced, est);
      var list = EL();
      if (list) {
        var entry = list.add(Object.assign({}, priced, { variantId: variantId }), raw, props);
        var lcart = list.toCart(), lline = lcart.items.filter(function (i) { return i.key === entry.id; })[0] || null;
        refreshBar(lcart);
        emit('mad:wizard:added', { mode: 'sheet', product: priced, raw: raw, line: lline, cart: lcart, estimateList: true });
        return { line: lline, cart: lcart, estimate: est };
      }
      var body = { items: [{ id: variantId, quantity: 1, properties: props }], sections: 'cart-icon-bubble', sections_url: window.location.pathname };
      return fetchJson('/cart/add.js', {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body),
      }).then(function (res) {
        var line = res.items ? res.items[0] : res;
        updateBubble(res.sections);
        return fetchCart().then(function (cart) {
          notifyCart(cart, { productVariantId: variantId });
          emit('mad:wizard:added', { mode: 'sheet', product: priced, raw: raw, line: line, cart: cart });
          return { line: line, cart: cart, estimate: est };
        });
      });
    });
  }

  /* ── sticky "Your estimate" bar ────────────────────────────────── */
  function rawOf(item) {
    var s = item && item.properties && item.properties._raw;
    if (!s) return null;
    try { return JSON.parse(s); } catch (e) { return null; }
  }

  // Lines with _raw count their estimate_total; lines without (Buy door only) count their line price.
  function quoteTotals(cart) {
    var items = (cart && cart.items) || [], count = 0, total = 0;
    items.forEach(function (it) {
      count += Number(it.quantity) || 0;
      var raw = rawOf(it), est = raw && parseFloat(raw.estimate_total);
      total += raw && !isNaN(est) ? est * (Number(it.quantity) || 1) : (Number(it.final_line_price) || 0) / 100;
    });
    return { count: count, total: Math.round(total * 100) / 100 };
  }

  function refreshBar(cart) {
    var bar = document.querySelector('[data-mad-quote-bar]');
    if (!bar || !window.MADEstimate) return;
    var t = quoteTotals(cart);
    var show = t.count > 0;
    bar.setAttribute('aria-hidden', show ? 'false' : 'true');
    document.body.classList.toggle('mad-quote-bar-visible', show);
    if (!show) return;
    var countEl = bar.querySelector('[data-mad-quote-bar-count]');
    var totalEl = bar.querySelector('[data-mad-quote-bar-total]');
    if (countEl) countEl.textContent = t.count + (t.count === 1 ? ' door' : ' doors');
    var fnote = bar.querySelector('[data-mad-freight-note]');
    if (fnote) fnote.textContent = M().freightText(t.count, BOOK(), true, 'collection'); /* no freight number on collection pages (8/21) */
    if (totalEl) totalEl.textContent = M().money(t.total);
    var finish = bar.querySelector('[data-mad-quote-bar-finish]');
    if (finish && CFG().cartUrl) finish.setAttribute('href', CFG().cartUrl);
  }

  function refreshBarFromServer() {
    return ready().then(function () { var list = EL(); return list ? list.toCart() : fetchCart(); }).then(refreshBar).catch(function () { /* bar stays hidden */ });
  }

  function watchKeyboard() {
    var vv = window.visualViewport;
    if (!vv) return;
    var base = vv.height;
    vv.addEventListener('resize', function () {
      if (vv.height > base) base = vv.height;
      document.documentElement.classList.toggle('mad-keyboard-open', base - vv.height > 150);
    });
  }

  function initBar() {
    if (!document.querySelector('[data-mad-quote-bar]')) return;
    refreshBarFromServer();
    ['mad:wizard:added', 'mad:wizard:updated', 'mad:wizard:removed', 'cart:updated', 'mad:estimate:changed'].forEach(function (name) {
      document.addEventListener(name, function (e) {
        if (EL()) { refreshBarFromServer(); return; } /* the bar counts the estimate list, not the payable cart */
        if (e.detail && e.detail.cart && e.detail.cart.items) refreshBar(e.detail.cart); else refreshBarFromServer();
      });
    });
    watchKeyboard();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initBar); else initBar();

  window.MadSheetCore = {
    ready: ready, productFromElement: productFromElement, ctxFor: ctxFor, installReadyPrice: installReadyPrice,
    resizePrice: resizePrice, fitFor: fitFor, quickRaw: quickRaw, estimateFor: estimateFor, buildProperties: buildProperties,
    fetchCart: fetchCart, addToCart: addToCart, quoteTotals: quoteTotals, refreshBar: refreshBar, emit: emit,
  };
})();
